import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  DataCaptureContext,
  DataCaptureView,
  FrameSourceState,
  Camera,
  CameraPosition,
  RectangularViewfinder,
  RectangularViewfinderStyle,
  AimerViewfinder,
  CameraSettings
} from "@scandit/web-datacapture-core";

import {
  BarcodeCapture,
  BarcodeCaptureSettings,
  BarcodeCaptureOverlay,
  Symbology,
  barcodeCaptureLoader,
  type BarcodeCaptureListener
} from "@scandit/web-datacapture-barcode";

export type ScanditViewfinder = "aimer" | "rectangular" | "none";

export interface ScanditBarcodeScannerProps {
  licenseKey: string;
  libraryLocation: string; // must point to sdc-lib/

  symbologies?: Symbology[];
  codeDuplicateFilter?: number;
  viewfinder?: ScanditViewfinder;

  paused?: boolean;

  onScan: (data: string, symbology: Symbology) => void;
  stopAfterScan?: boolean;

  className?: string;
  style?: React.CSSProperties;
}

type SdkHandle = {
  context: DataCaptureContext;
  view: DataCaptureView;
  camera: Camera;
  barcodeCapture: BarcodeCapture;
  listener: BarcodeCaptureListener;
};

function normalizeLibraryLocation(loc: string) {
  const trimmed = (loc ?? "").trim();
  if (!trimmed) return "";
  return trimmed.endsWith("/") ? trimmed : `${trimmed}/`;
}

export function ScanditBarcodeScanner(props: ScanditBarcodeScannerProps) {
  const {
    licenseKey,
    libraryLocation,
    symbologies = [Symbology.EAN8, Symbology.EAN13UPCA, Symbology.Code128],
    codeDuplicateFilter = -1,
    viewfinder = "rectangular",
    paused = false,
    onScan,
    stopAfterScan = false,
    className,
    style
  } = props;

  const hostRef = useRef<HTMLDivElement | null>(null);
  const sdkRef = useRef<SdkHandle | null>(null);
  const runIdRef = useRef(0);

  const [error, setError] = useState<string | null>(null);

  const onScanRef = useRef(onScan);
  const stopAfterScanRef = useRef(stopAfterScan);

  useEffect(() => {
    onScanRef.current = onScan;
    stopAfterScanRef.current = stopAfterScan;
  }, [onScan, stopAfterScan]);

  const symbologyKey = useMemo(
    () => symbologies.slice().sort().join("|"),
    [symbologies]
  );

  const normalizedLibraryLocation = useMemo(
    () => normalizeLibraryLocation(libraryLocation),
    [libraryLocation]
  );

  useEffect(() => {
    let cancelled = false;
    const runId = ++runIdRef.current;

    const cleanupHandle = async (sdk: SdkHandle | null) => {
      if (!sdk) return;

      try {
        sdk.barcodeCapture.removeListener(sdk.listener);
      } catch {}

      try {
        await sdk.barcodeCapture.setEnabled(false);
      } catch {}

      try {
        await sdk.camera.switchToDesiredState(FrameSourceState.Off);
      } catch {}

      try {
        sdk.view.detachFromElement();
      } catch {}

      try {
        await sdk.context.dispose();
      } catch {}
    };

    const cleanupCurrent = async () => {
      const current = sdkRef.current;
      sdkRef.current = null;
      await cleanupHandle(current);
    };

    const init = async () => {
      setError(null);

      if (!hostRef.current) return;

      if (!licenseKey?.trim() || !normalizedLibraryLocation) return;

      await cleanupCurrent();
      if (cancelled || runId !== runIdRef.current) return;

      let local: SdkHandle | null = null;

      try {
        const context = await DataCaptureContext.forLicenseKey(licenseKey, {
          libraryLocation: normalizedLibraryLocation,
          moduleLoaders: [barcodeCaptureLoader()]
        });

        if (cancelled || runId !== runIdRef.current) {
          await context.dispose();
          return;
        }

        const view = await DataCaptureView.forContext(context);
        if (cancelled || runId !== runIdRef.current) {
          await context.dispose();
          return;
        }
        view.connectToElement(hostRef.current);

        const camera = Camera.pickBestGuessForPosition(
          CameraPosition.WorldFacing
        );
        const camSettings = new CameraSettings(
          BarcodeCapture.recommendedCameraSettings
        );

        camSettings.zoomFactor = 1.0;

        camSettings.zoomGestureZoomFactor = 1.0;

        await camera.applySettings(camSettings);
        await context.setFrameSource(camera);

        const settings = new BarcodeCaptureSettings();
        settings.enableSymbologies(symbologies);
        settings.codeDuplicateFilter = codeDuplicateFilter;

        const barcodeCapture = await BarcodeCapture.forContext(
          context,
          settings
        );

        const listener: BarcodeCaptureListener = {
          didScan: async (mode, session) => {
            const barcode = session.newlyRecognizedBarcode;
            if (!barcode?.data) return;

            onScanRef.current(barcode.data, barcode.symbology);

            if (stopAfterScanRef.current) {
              await mode.setEnabled(false);
            }
          }
        };
        barcodeCapture.addListener(listener);

        const overlay = await BarcodeCaptureOverlay.withBarcodeCaptureForView(
          barcodeCapture,
          view
        );

        if (viewfinder === "aimer") {
          await overlay.setViewfinder(new AimerViewfinder());
        } else if (viewfinder === "rectangular") {
          await overlay.setViewfinder(
            new RectangularViewfinder(RectangularViewfinderStyle.Rounded)
          );
        } else {
          await overlay.setViewfinder(null);
        }

        await barcodeCapture.setEnabled(!paused);
        await camera.switchToDesiredState(
          paused ? FrameSourceState.Off : FrameSourceState.On
        );

        local = { context, view, camera, barcodeCapture, listener };

        if (cancelled || runId !== runIdRef.current) {
          await cleanupHandle(local);
          return;
        }

        sdkRef.current = local;
      } catch (e: any) {
        setError(e?.message ?? String(e));
        await cleanupHandle(local);
        await cleanupCurrent();
      }
    };

    void init();

    return () => {
      cancelled = true;
      void cleanupCurrent();
    };
  }, [
    licenseKey,
    normalizedLibraryLocation,
    symbologyKey,
    codeDuplicateFilter,
    viewfinder,
    paused
  ]);

  return (
    <div className={className} style={{ position: "relative", ...style }}>
      <div ref={hostRef} style={{ width: "100%", height: "100%" }} />
      {error && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 12,
            background: "rgba(0,0,0,0.5)",
            color: "white",
            fontSize: 14,
            textAlign: "center"
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
